/**
 * Background health sync utility.
 *
 * Called on app foreground and after session restore. Silently reads device
 * health data (Apple Health on iOS, Health Connect on Android) for every
 * project that has the integration enabled, then posts it to the backend.
 *
 * Throttled to at most once every SYNC_THROTTLE_MS per project so it doesn't
 * hammer the API on rapid foreground events.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { store } from '../store';
import { fetchHealthSettings, syncHealthData } from '../store/actions/health';
import { fetchProjects } from '../store/actions/projects';
import { HEALTH_CATEGORIES } from './healthTypes';
import { localDayKey } from './healthDayKey';
import { classifyHealthKitSource, classifyHealthConnectSource } from './healthSource';
import { syncClinicalRecords } from './clinicalSync';
import debug from './debug';
import { apiPost, apiProjectsPost } from './apiClient';

// ─── Health Alert constants ───────────────────────────────────────────────────

// HealthKit category type identifiers for event-based system alerts.
// These are HKCategoryTypeIdentifier values (strings) used with subscribeToChanges.
const HK_ALERT_TYPES: Record<string, string> = {
  fall_detection:    'HKCategoryTypeIdentifierAppleFallDetection',
  irregular_rhythm:  'HKCategoryTypeIdentifierIrregularHeartRhythmEvent',
  high_heart_rate:   'HKCategoryTypeIdentifierHighHeartRateEvent',
  low_heart_rate:    'HKCategoryTypeIdentifierLowHeartRateEvent',
};

/**
 * How recent a HealthKit event must be for us to treat it as happening NOW.
 *
 * subscribeToChanges fires when the STORE changes, not when the event occurred.
 * A phone that syncs health data from another source — or any re-sync that
 * backfills history — therefore delivers a change notification for a record
 * that may be days or years old, and we announced it as a live event.
 * Generous enough to survive delayed delivery of a real event.
 */
export const EVENT_FRESHNESS_MINUTES = 60;

/**
 * Whether an event is old enough that announcing it would be wrong.
 *
 * Exported and pure so the rule can be tested directly: it decides whether a
 * person is told their heart is in an abnormal rhythm right now.
 *
 * @param eventAtMs when the event occurred, or null when that cannot be read.
 *   Null means "cannot tell" and never suppresses — a missed real event costs
 *   more than a spurious one.
 */
export const isStaleHealthEvent = (
  alertType: string,
  eventAtMs: number | null,
  nowMs: number = Date.now(),
): boolean => {
  // Fall detection escalates to an emergency SOS. Staying silent on a real
  // fall is the worse error, so it is never suppressed on age.
  if (alertType === 'fall_detection') return false;
  if (eventAtMs == null) return false;
  return (nowMs - eventAtMs) / 60000 > EVENT_FRESHNESS_MINUTES;
};

/**
 * When a category event actually happened, or null when it cannot be read.
 *
 * The distinction matters: "the newest event is from last year" is grounds to
 * stay silent, while "the query failed" is not — the second must never
 * suppress an alert, because a missed real event costs more than a spurious
 * one. Callers treat null as "cannot tell" and proceed.
 */
async function latestCategoryEventAtMs(hkIdentifier: string): Promise<number | null> {
  try {
    const samples = await HealthKit.queryCategorySamples(hkIdentifier, {
      filter: { date: { startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), endDate: new Date() } },
      limit: 10,
    });
    if (!Array.isArray(samples) || samples.length === 0) return null;
    const times = samples
      .map((sample: any) => new Date(sample?.endDate || sample?.startDate).getTime())
      .filter((ms: number) => Number.isFinite(ms));
    return times.length ? Math.max(...times) : null;
  } catch {
    return null;
  }
}

// AsyncStorage keys for client-side health alert cooldowns.
const alertCooldownKey = (projectId: string | number, key: string) =>
  `health_alert_cooldown_${projectId}_${key}`;

// subscriptions map for health alert event observers (keyed by projectId:alertType)
const healthAlertSubscriptions = new Map<string, { remove: () => boolean | void }>();

// Set to 12 min so the 15-min foreground interval (and background fetch task)
// always clears the throttle — preventing the off-by-one skip where completion
// time and interval fire at the same boundary and every other tick gets blocked.
const SYNC_THROTTLE_MS = 12 * 60 * 1000;

/**
 * HealthKit reports HKUnit.percent() values as FRACTIONS, not 0–100. A blood
 * oxygen of 97% arrives as 0.97 and a walking steadiness of 80% as 0.8, both
 * carrying the unit string "%".
 *
 * Nothing scaled them, so the app rendered "Blood Oxygen 1 %" and "Walking
 * Steadiness 0.8 %" — numbers that are alarming, wrong, and quietly teach a
 * family that the health data cannot be trusted. In a product where a care
 * team reads these, that is worse than showing nothing.
 *
 * The Android path never had this bug: every Health Connect percentage metric
 * declares an hcValueExtractor that multiplies by 100 (see
 * healthTypes.ts — OxygenSaturation and friends). This brings iOS in line.
 *
 * Scoped to the percent unit specifically, and idempotent in the sense that a
 * value already above 1 is left alone: HealthKit will not report 97 for a
 * fraction-unit sample, so a >1 reading means some other source already
 * scaled it and doubling would be the new bug.
 */
function normalizeHealthKitValue(value: any, unit: any): number {
  const n = Number(value) || 0;
  const u = String(unit || '').trim();
  if (u !== '%') return n;
  return n > 1 ? n : Math.round(n * 100 * 10) / 10;
}

// In-memory throttle map: projectId → last sync timestamp
// Resets on app kill, which is intentional (cold launch always syncs).
const lastSyncAt = new Map<string, number>();
// Consecutive upload failures per plan, and the time before which we must not
// try again. Cleared on the first success.
const syncFailures = new Map<string, number>();
const syncBackoffUntil = new Map<string, number>();
const SYNC_FAILURE_BACKOFF_CAP_MS = 60 * 60 * 1000;

// ─── Lazy platform library handles ───────────────────────────────────────────

let HealthKit: any = null;
if (Platform.OS === 'ios') {
  try {
    const hk = require('@kingstinct/react-native-healthkit');
    HealthKit = hk.default || hk;
  } catch (_) {}
}

// projectId:typeIdentifier -> HealthKit subscription
const healthObserverSubscriptions = new Map<string, { remove: () => boolean | void }>();
const backgroundDeliveryEnabled = new Set<string>();

let HC: any = null;
if (Platform.OS === 'android') {
  try {
    HC = require('react-native-health-connect');
  } catch (_) {}
}

function getAndroidSupportedRecordTypes(): Set<string> {
  if (!HC?.RecordType || typeof HC.RecordType !== 'object') return new Set();
  return new Set(
    Object.values(HC.RecordType).filter((value): value is string => typeof value === 'string' && value.length > 0),
  );
}

// ─── Core reading function ────────────────────────────────────────────────────

async function readDeviceHealthData(
  selectedTypes: string[],
): Promise<{ readings: Record<string, any[]>; readFailed: boolean }> {
  const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const to = new Date();
  const readings: Record<string, any[]> = {};
  const allConfigs = HEALTH_CATEGORIES.flatMap(c => c.types);
  // Distinguishes "the device had nothing new" from "we could not read the
  // device at all". Only the first should stamp the sync throttle.
  let readFailed = false;

  if (Platform.OS === 'ios' && HealthKit) {
    // Ask before reading, exactly as HealthIntegrationScreen does. HealthKit
    // permissions can be reset by an App Store / TestFlight update, and
    // without this a reset turns into permanent silence on the background
    // path while the manual sync screen keeps working.
    try {
      // The alert categories have to be in this list, not just the sync types.
      // Without read access, queryCategorySamples for an alert type can never
      // return a sample, latestCategoryEventAtMs is permanently null, and the
      // staleness gate below — which only runs when it has a timestamp — is
      // skipped for every alert it exists to filter. Asking for them is what
      // makes that gate able to answer at all.
      const identifiers = [...new Set([
        ...selectedTypes
          .map(key => allConfigs.find(t => t.key === key)?.hkIdentifier)
          .filter(Boolean),
        ...Object.values(HK_ALERT_TYPES),
      ])];
      if (identifiers.length > 0) {
        await HealthKit.requestAuthorization({ toRead: identifiers });
      }
    } catch (_) { /* denial is not a read failure — the queries below no-op */ }

    for (const typeKey of selectedTypes) {
      const cfg = allConfigs.find(t => t.key === typeKey);
      if (!cfg) continue;
      try {
        let samples: any[] = [];
        // `queryCategorySamples` / `queryQuantitySamples`, NOT the `…Raw`
        // variants this used to call.
        //
        // @kingstinct/react-native-healthkit has never exported a
        // `queryQuantitySamplesRaw` or `queryCategorySamplesRaw`. The module
        // handle is an untyped `any` from require(), so `undefined(...)` threw
        // a TypeError on every single type, on every sync, and the bare
        // `catch {}` below swallowed it — leaving `readings` empty and the
        // whole thing indistinguishable from "no new data". Every background
        // and on-focus health sync on iOS has been a no-op since this was
        // written; only HealthIntegrationScreen's manual sync, which calls the
        // real API, ever moved data.
        //
        // The options shape changed too: v13 nests the window under
        // `filter.date` and has no top-level `from`/`to`. The native bridge
        // ignores unknown keys rather than throwing, so passing the old shape
        // would silently drop the date window and return the newest N samples
        // instead — a quieter bug than the first, and the reason both had to
        // be corrected together.
        const filter = { date: { startDate: from, endDate: to } };
        if (cfg.isCategoryType) {
          samples = await HealthKit.queryCategorySamples(cfg.hkIdentifier, { filter, limit: 200 });
        } else {
          samples = await HealthKit.queryQuantitySamples(cfg.hkIdentifier, { filter, limit: 200 });
        }
        if (samples?.length > 0) {
          // For duration-based category types (sleep, mindfulness), compute the
          // sample value as minutes from startDate→endDate instead of using the
          // raw HKCategoryValue integer (which is meaningless for time-series storage).
          if (cfg.isDurationBased) {
            let filteredSamples = samples;
            if (cfg.sleepAsleepOnly) {
              // Only accumulate samples representing sleep (not inBed=0 or awake=2).
              // HKCategoryValueSleepAnalysis: 1=asleep(legacy), 3=core, 4=deep, 5=REM
              filteredSamples = samples.filter((s: any) => [1, 3, 4, 5].includes(Number(s.value)));
            }
            readings[typeKey] = filteredSamples
              .map((s: any) => {
                const start = s.startDate ? new Date(s.startDate).getTime() : 0;
                const end = s.endDate ? new Date(s.endDate).getTime() : 0;
                const minutes = end > start ? Math.round((end - start) / 60000) : 0;
                if (minutes <= 0) return null;
                return {
                  value: minutes,
                  unit: 'min',
                  // Without `day` the backend files this under its UTC date, so
                  // this sync and the manual one disagree about which day it was.
                  day: localDayKey(start),
                  startDate: typeof s.startDate === 'string' ? s.startDate : new Date(s.startDate).toISOString(),
                  endDate: typeof s.endDate === 'string' ? s.endDate : new Date(s.endDate).toISOString(),
                  source: classifyHealthKitSource(s),
                };
              })
              .filter(Boolean);
          } else {
            readings[typeKey] = samples.map((s: any) => ({
              value: normalizeHealthKitValue(s.quantity ?? s.value ?? 0, s.unit),
              unit: s.unit || '',
              day: localDayKey(new Date(s.startDate).getTime()),
              startDate: typeof s.startDate === 'string' ? s.startDate : new Date(s.startDate).toISOString(),
              endDate: s.endDate
                ? (typeof s.endDate === 'string' ? s.endDate : new Date(s.endDate).toISOString())
                : undefined,
              // Provenance: the escalation gate treats a recent heart-rate
              // sample as evidence of a worn watch, and must be able to tell
              // that from a hand-typed number. See utilities/healthSource.
              source: classifyHealthKitSource(s),
            }));
          }
          if (readings[typeKey]?.length === 0) delete readings[typeKey];
        }
      } catch (err: any) {
        // Was `catch (_) {}`. A silent catch here is what let a call to a
        // nonexistent HealthKit method look identical to a quiet day for as
        // long as this file has existed.
        readFailed = true;
        debug.warn(`[health] HealthKit read failed for ${typeKey}: ${err?.message ?? err}`);
      }
    }
  } else if (Platform.OS === 'android' && HC) {
    try {
      await HC.initialize();
    } catch (err: any) {
      debug.warn(`[health] Health Connect initialize failed: ${err?.message ?? err}`);
      return { readings, readFailed: true };
    }

    const timeRangeFilter = {
      operator: 'between',
      startTime: from.toISOString(),
      endTime: to.toISOString(),
    };
    const fetchedRecordTypes = new Map<string, any[]>();
    const supportedRecordTypes = getAndroidSupportedRecordTypes();

    for (const typeKey of selectedTypes) {
      const cfg = allConfigs.find(t => t.key === typeKey);
      if (!cfg?.hcRecordType || !cfg.hcValueExtractor) continue;
      if (supportedRecordTypes.size > 0 && !supportedRecordTypes.has(cfg.hcRecordType)) continue;
      try {
        let records: any[];
        if (fetchedRecordTypes.has(cfg.hcRecordType)) {
          records = fetchedRecordTypes.get(cfg.hcRecordType)!;
        } else {
          const result = await HC.readRecords(cfg.hcRecordType, { timeRangeFilter });
          records = result?.records ?? [];
          fetchedRecordTypes.set(cfg.hcRecordType, records);
        }
        const mapped = records
          .map((r: any) => {
            const extracted = cfg.hcValueExtractor!(r);
            if (!extracted) return null;
            const startDate = r.time ?? r.startTime;
            const endDate = r.endTime ?? r.time;
            return {
              value: extracted.value,
              unit: extracted.unit,
              day: localDayKey(new Date(startDate).getTime()),
              startDate: typeof startDate === 'string' ? startDate : new Date(startDate).toISOString(),
              endDate: endDate
                ? (typeof endDate === 'string' ? endDate : new Date(endDate).toISOString())
                : undefined,
              source: classifyHealthConnectSource(r),
            };
          })
          .filter(Boolean);
        if (mapped.length > 0) {
          readings[typeKey] = mapped;
        }
      } catch (_) {}
    }
  }

  return { readings, readFailed };
}

function resolveProjectId(project: any): string | number | null {
  const value = project?.project_id ?? project?.id ?? null;
  return value == null ? null : value;
}

async function ensureIOSHealthObservers(
  projectId: number | string,
  selectedTypes: string[],
): Promise<void> {
  if (Platform.OS !== 'ios' || !HealthKit) return;

  const allConfigs = HEALTH_CATEGORIES.flatMap(c => c.types);
  const candidates = selectedTypes
    .map((typeKey) => allConfigs.find((cfg) => cfg.key === typeKey))
    .filter(Boolean) as Array<{ hkIdentifier: string }>;
  const candidateSet = new Set(candidates.map((c) => c.hkIdentifier));

  // Remove stale subscriptions for this project when selected types change.
  Array.from(healthObserverSubscriptions.keys()).forEach((subKey) => {
    if (!subKey.startsWith(`${projectId}:`)) return;
    const typeIdentifier = subKey.slice(String(projectId).length + 1);
    if (candidateSet.has(typeIdentifier)) return;
    const existing = healthObserverSubscriptions.get(subKey);
    try {
      existing?.remove?.();
    } catch (_) {}
    healthObserverSubscriptions.delete(subKey);
  });

  await Promise.allSettled(
    candidates.map(async (cfg) => {
      const typeIdentifier = cfg.hkIdentifier;

      if (!backgroundDeliveryEnabled.has(typeIdentifier)) {
        try {
          // Ask iOS to wake the app when this HealthKit type changes.
          const immediate = HealthKit?.UpdateFrequency?.immediate ?? 1;
          await HealthKit.enableBackgroundDelivery(typeIdentifier, immediate);
          backgroundDeliveryEnabled.add(typeIdentifier);
          debug.log('ui', 'Enabled HealthKit background delivery', { typeIdentifier });
        } catch (err) {
          debug.warn('Failed to enable HealthKit background delivery', { typeIdentifier, err });
        }
      }

      const subKey = `${projectId}:${typeIdentifier}`;
      if (healthObserverSubscriptions.has(subKey)) return;

      try {
        const subscription = HealthKit.subscribeToChanges(
          typeIdentifier,
          (_args: { typeIdentifier: string; errorMessage?: string }) => {
            // Piggyback on native HealthKit update events to force a fresh sync.
            runHealthSync(true, projectId).catch(() => {});
          },
        );
        healthObserverSubscriptions.set(subKey, subscription);
      } catch (err) {
        debug.warn('Failed to subscribe to HealthKit changes', { typeIdentifier, err });
      }
    }),
  );
}

// ─── Health Alert helpers ─────────────────────────────────────────────────────

function getSessionIds(): { accountId: string; cognitoId: string } {
  const state = store.getState() as any;
  return {
    accountId: state.session?.account?.account_id ?? '',
    cognitoId: state.authentication?.user?.cognito_id ?? '',
  };
}

/** Check and set a client-side cooldown via AsyncStorage. Returns true if within cooldown. */
async function isWithinCooldown(
  projectId: string | number,
  key: string,
  cooldownMinutes: number,
): Promise<boolean> {
  try {
    const raw = await AsyncStorage.getItem(alertCooldownKey(projectId, key));
    if (!raw) return false;
    const elapsed = (Date.now() - Number(raw)) / 60000;
    return elapsed < cooldownMinutes;
  } catch {
    return false;
  }
}

async function setCooldown(projectId: string | number, key: string): Promise<void> {
  try {
    await AsyncStorage.setItem(alertCooldownKey(projectId, key), String(Date.now()));
  } catch {}
}

/** Fire and forget — calls the health-alert backend endpoint. */
async function fireHealthAlert(
  projectId: string | number,
  alertType: string,
  ruleId: string | null,
  context: Record<string, any>,
): Promise<void> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return;
  try {
    await apiProjectsPost(`/health-alert/${projectId}/${accountId}/${cognitoId}`,
      { alert_type: alertType, rule_id: ruleId, context },
    );
  } catch (err: any) {
    debug.warn('[health-alert] Failed to send health alert', { alertType, err: err?.message });
  }
}

/** Fire and forget — routes fall detection through the existing emergency-sos endpoint. */
async function fireFallDetectionSOS(
  projectId: string | number,
  context: Record<string, any>,
): Promise<void> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return;
  try {
    await apiProjectsPost(`/emergency-sos/${projectId}/${accountId}/${cognitoId}`,
      {
        impactDetected: true,
        latitude:       context.latitude ?? null,
        longitude:      context.longitude ?? null,
        heartRate:      context.heartRate ?? null,
        activityState:  context.activityState ?? null,
        triggeredByRole: 'health_alert',
      },
    );
  } catch (err: any) {
    debug.warn('[health-alert] Failed to send fall-detection SOS', { err: err?.message });
  }
}

/**
 * Registers HealthKit category event observers for health alert types
 * (fall detection, irregular rhythm, high/low heart rate).
 *
 * Called once per project when health alerts are enabled.
 * Fall detection routes through emergency-sos; others route through health-alert.
 */
async function ensureHealthAlertObservers(
  projectId: string | number,
  config: any,
): Promise<void> {
  if (Platform.OS !== 'ios' || !HealthKit) return;
  if (!config?.enabled) return;

  const systemAlerts = config.system_alerts ?? {};

  for (const [alertType, hkIdentifier] of Object.entries(HK_ALERT_TYPES)) {
    const alertCfg = systemAlerts[alertType];

    const subKey = `${projectId}:alert:${alertType}`;

    if (!alertCfg?.enabled) {
      // Remove subscription if alert was disabled.
      if (healthAlertSubscriptions.has(subKey)) {
        try { healthAlertSubscriptions.get(subKey)?.remove?.(); } catch {}
        healthAlertSubscriptions.delete(subKey);
      }
      continue;
    }

    if (healthAlertSubscriptions.has(subKey)) continue;

    try {
      const subscription = HealthKit.subscribeToChanges(
        hkIdentifier,
        async (args: { typeIdentifier: string; errorMessage?: string }) => {
          // An error delivery is not a detection. HealthKit invokes this handler
          // with errorMessage set when the query itself failed — most commonly
          // because read access to this category was never granted — and the
          // argument was previously ignored entirely, so a permissions failure
          // was announced to a caregiver as a cardiac event.
          if (args?.errorMessage) {
            debug.warn('[health-alert] Observer reported an error, not an event', {
              projectId, alertType, err: args.errorMessage,
            });
            return;
          }

          const cooldownMinutes: number = alertCfg.cooldown_minutes ?? config.cooldown_minutes ?? 60;
          const withinCooldown = await isWithinCooldown(projectId, alertType, cooldownMinutes);
          if (withinCooldown) return;

          // Read the most recent heart-rate sample for context and stale-event
          // filtering (helps avoid delayed/no-watch false positives).
          let heartRate: number | null = null;
          let heartRateSampleAtMs: number | null = null;
          try {
            const hkHR = 'HKQuantityTypeIdentifierHeartRate';
            // Same dead-API fix as readDeviceHealthData above — this path
            // fed the high/low heart-rate alert evidence and was equally inert.
            const samples = await HealthKit.queryQuantitySamples(hkHR, {
              filter: { date: { startDate: new Date(Date.now() - 30 * 60 * 1000), endDate: new Date() } },
              limit: 5,
            });
            const latest = Array.isArray(samples) ? samples.find((s: any) => s?.quantity != null) : null;
            if (latest?.quantity != null) {
              heartRate = Math.round(latest.quantity);
              if (latest.startDate) {
                heartRateSampleAtMs = new Date(latest.startDate).getTime();
              }
            }
          } catch {}

          const context: Record<string, any> = {};
          if (heartRate != null) context.heartRate = heartRate;
          if (heartRateSampleAtMs) context.heartRateSampleAt = new Date(heartRateSampleAtMs).toISOString();

          // When did the EVENT happen, as opposed to when did HealthKit tell us
          // about it? Only the second is implied by this callback firing.
          const eventAtMs = await latestCategoryEventAtMs(hkIdentifier);
          if (eventAtMs != null) {
            context.eventOccurredAt = new Date(eventAtMs).toISOString();
            const eventAgeMinutes = (Date.now() - eventAtMs) / 60000;
            context.eventAgeMinutes = Math.round(eventAgeMinutes);
            // Stale means the store was updated with history, not that anything
            // happened. Fall detection is exempt: it escalates to an emergency
            // SOS, and staying silent on a real fall is the worse error.
            if (isStaleHealthEvent(alertType, eventAtMs)) {
              debug.log('ui', '[health-alert] Skipping stale event', { projectId, alertType, eventAgeMinutes: Math.round(eventAgeMinutes) });
              return;
            }
          }

          // High/low HR notifications must include a fresh value; skip delayed
          // events where the latest sample is too old.
          if (alertType === 'high_heart_rate' || alertType === 'low_heart_rate') {
            if (heartRate == null || heartRateSampleAtMs == null) return;
            const ageMinutes = (Date.now() - heartRateSampleAtMs) / 60000;
            if (ageMinutes > 15) return;

            const threshold = Number(alertCfg?.threshold);
            if (Number.isFinite(threshold) && threshold > 0) {
              if (alertType === 'high_heart_rate' && heartRate < threshold) return;
              if (alertType === 'low_heart_rate' && heartRate > threshold) return;
            }
          }

          await setCooldown(projectId, alertType);

          if (alertType === 'fall_detection') {
            await fireFallDetectionSOS(projectId, context).catch(() => {});
          } else {
            await fireHealthAlert(projectId, alertType, null, context).catch(() => {});
          }
        },
      );
      healthAlertSubscriptions.set(subKey, subscription);
      debug.log('ui', '[health-alert] Registered health alert observer', { projectId, alertType });
    } catch (err) {
      debug.warn('[health-alert] Failed to subscribe to health alert event', { alertType, err });
    }
  }
}

// ─── Custom rule evaluator ────────────────────────────────────────────────────

type HealthSamples = Record<string, Array<{ value: number; unit: string; startDate: string; endDate?: string }>>;

/**
 * Evaluates all enabled custom rules in a project's health_alerts_config against
 * fresh HealthKit samples and the current Redux/AsyncStorage state.
 *
 * Called at the end of each health sync cycle.
 */
export async function evaluateCustomRules(
  projectId: string | number,
  freshSamples: HealthSamples,
): Promise<void> {
  if (Platform.OS !== 'ios' || !HealthKit) return;

  const state = store.getState() as any;
  const projects: Record<string, any> = state.projects?.list ?? {};
  const project = Object.values(projects).find(
    (p: any) => String(resolveProjectId(p)) === String(projectId),
  );

  const alertsConfig = project?.health_alerts_config;
  if (!alertsConfig?.enabled) return;

  const customRules: any[] = alertsConfig.custom_rules ?? [];
  const globalCooldown: number = alertsConfig.cooldown_minutes ?? 60;

  // Evaluate each enabled rule.
  await Promise.allSettled(
    customRules
      .filter((rule: any) => rule?.enabled && Array.isArray(rule.conditions))
      .map(async (rule: any) => {
        const cooldownMinutes: number = rule.cooldown_minutes ?? globalCooldown;
        const withinCooldown = await isWithinCooldown(projectId, rule.id, cooldownMinutes);
        if (withinCooldown) return;

        const logic: string = rule.logic ?? 'AND';
        const results = await Promise.all(
          rule.conditions.map((cond: any) => evaluateCondition(cond, freshSamples, projectId, state)),
        );

        const passed = logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
        if (!passed) return;

        await setCooldown(projectId, rule.id);
        await fireHealthAlert(projectId, 'custom_rule', rule.id, {}).catch(() => {});
      }),
  );
}

/** Evaluates a single rule condition. Returns true if the condition is satisfied. */
async function evaluateCondition(
  cond: any,
  freshSamples: HealthSamples,
  projectId: string | number,
  reduxState: any,
): Promise<boolean> {
  if (!cond?.type) return false;

  switch (cond.type) {
    case 'metric_threshold': {
      const key: string = cond.metric;
      const threshold: number = cond.threshold;
      const operator: string = cond.operator ?? '>';
      const windowMs: number = (cond.sample_window_minutes ?? 10) * 60 * 1000;
      const minSamples: number = cond.min_samples ?? 1;

      const cutoff = new Date(Date.now() - windowMs).toISOString();
      const samples = (freshSamples[key] ?? []).filter(
        (s) => (s.startDate ?? '') >= cutoff,
      );
      if (samples.length < minSamples) return false;

      return samples.some((s) => compareWithOperator(s.value, operator, threshold));
    }

    case 'metric_absent': {
      const key: string = cond.metric;
      const withinMs = (cond.within_hours ?? 24) * 3600 * 1000;
      const cutoff = new Date(Date.now() - withinMs).toISOString();
      const recent = (freshSamples[key] ?? []).filter((s) => (s.startDate ?? '') >= cutoff);
      // Condition is true when no readings exist within the window.
      return recent.length === 0;
    }

    case 'smart_reminder_active': {
      const smartKey: string = cond.smart_key;
      const projects: Record<string, any> = reduxState.projects?.list ?? {};
      const project = Object.values(projects).find(
        (p: any) => String(resolveProjectId(p)) === String(projectId),
      );
      if (!project) return false;
      const reminders: any[] = reduxState.reminders?.list ?? [];
      return reminders.some(
        (r: any) =>
          String(r.project_id) === String(resolveProjectId(project)) &&
          r.config?.smart_key === smartKey &&
          (r.status === 'scheduled' || r.status === 'pending'),
      );
    }

    case 'location_stale': {
      const withinHours: number = cond.within_hours ?? 4;
      try {
        const raw = await AsyncStorage.getItem(`location_last_sync_${projectId}`);
        if (!raw) return true; // No record at all → definitely stale
        const elapsed = (Date.now() - Number(raw)) / 3600000;
        return elapsed > withinHours;
      } catch {
        return false;
      }
    }

    default:
      return false;
  }
}

function compareWithOperator(value: number, operator: string, threshold: number): boolean {
  switch (operator) {
    case '>':  return value > threshold;
    case '>=': return value >= threshold;
    case '<':  return value < threshold;
    case '<=': return value <= threshold;
    case '=':
    case '==': return value === threshold;
    default:   return false;
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Silently syncs health data for all projects that have the integration
 * enabled. Safe to call on every foreground event — throttled internally.
 *
 * @returns what actually happened — see HealthSyncOutcome. A caller that
 *   reports success to a caregiver MUST read this rather than assume the
 *   absence of a throw means data moved.
 *
 * @param force  Skip throttle check (e.g. after manual toggle-on)
 * @param targetProjectId Optional project filter for remote pull requests
 */
/**
 * Why a sync did or didn't move data.
 *
 * `runHealthSync` returned void, so every caller treated "it didn't throw" as
 * "it worked" — including the remote-sync handler, which reported `completed`
 * to a caregiver whether or not a single reading was uploaded. A denied
 * HealthKit permission, an ownership skip, a throttle, or (before #2280) a
 * call to a method that does not exist all looked identical to a real sync.
 */
export type HealthSyncReason =
  | 'uploaded'
  | 'no_projects'
  | 'not_native'
  | 'throttled'
  | 'not_enabled'
  | 'not_owner'
  | 'read_failed'
  | 'no_new_data'
  | 'upload_failed';

export type HealthSyncOutcome = { uploaded: boolean; reason: HealthSyncReason };

export async function runHealthSync(force = false, targetProjectId?: number | string): Promise<HealthSyncOutcome> {
  let state = store.getState() as any;
  let projectsMap: Record<string, any> = state.projects?.list ?? {};
  let projects = Object.values(projectsMap).filter((project: any) => {
    if (targetProjectId == null) return true;
    return String(resolveProjectId(project)) === String(targetProjectId);
  });

  // Background wake-ups can occur before project lists are fetched in UI flows.
  // Try a one-time lazy fetch so sync isn't blocked on opening a specific screen.
  if (projects.length === 0) {
    const accountId = state.session?.account?.account_id;
    const cognitoId = state.authentication?.user?.cognito_id;
    if (accountId && cognitoId) {
      await store.dispatch(fetchProjects(accountId, cognitoId) as any).catch(() => {});
      state = store.getState() as any;
      projectsMap = state.projects?.list ?? {};
      projects = Object.values(projectsMap).filter((project: any) => {
        if (targetProjectId == null) return true;
        return String(resolveProjectId(project)) === String(targetProjectId);
      });
    }
  }
  if (projects.length === 0) return { uploaded: false, reason: 'no_projects' };
  const nativeAvailable =
    (Platform.OS === 'ios' && !!HealthKit) ||
    (Platform.OS === 'android' && !!HC);
  if (!nativeAvailable) return { uploaded: false, reason: 'not_native' };

  // Fire syncs for all projects in parallel, each independently throttled
  const outcomes = await Promise.allSettled(
    projects.map(async (project: any): Promise<HealthSyncReason> => {
      const projectId = resolveProjectId(project);
      if (!projectId) return 'no_projects' as HealthSyncReason;

      // Upload-failure backoff, checked BEFORE the ordinary throttle and
      // deliberately NOT bypassed by `force`.
      //
      // Every HealthKit change observer calls runHealthSync(true, …), and there
      // are 42 of them — one per selected type — so a watch flushing a batch
      // fires many at once, all forced. `force` means "the device says there is
      // new data", which is not a reason to ignore that the server is currently
      // rejecting our writes. Without this the failing case retries hardest,
      // against a server-side design that keeps a plan's whole health record in
      // one item on one partition key.
      const backoffUntil = syncBackoffUntil.get(String(projectId)) ?? 0;
      if (Date.now() < backoffUntil) return 'throttled' as HealthSyncReason;

      // Throttle check
      if (!force) {
        const last = lastSyncAt.get(String(projectId)) ?? 0;
        if (Date.now() - last < SYNC_THROTTLE_MS) return 'throttled' as HealthSyncReason;
      }

      try {
        // Fetch this project's health settings
        const settingsRes = await store.dispatch(fetchHealthSettings(projectId) as any);
        const settingsFetched = !!settingsRes?.success;

        // Only the device that OWNS the integration may upload for this plan.
        //
        // The sync endpoint authorizes on account membership alone, and this
        // function runs for EVERY project in the store on every foreground. So
        // without this check a caregiver who has granted HealthKit access on
        // their own phone uploads their own steps, heart rate and sleep into
        // the recipient's health_data# item — the same store the wellness
        // baselines, the "usual range" bands and the escalation gate all read
        // from. Nothing downstream can tell whose body it came from.
        //
        // The exclusive lock already exists (one enabling cognito_id per plan,
        // surfaced by get-settings precisely so clients can enforce it) and
        // HealthIntegrationScreen honours it; the background path never did.
        // A caregiver who wants fresh data uses requestRemoteHealthSync, which
        // asks the recipient's own device to sync.
        //
        // Fails closed on a missing owner: stale data is visible and
        // recoverable, a stranger's vitals merged into a care record is not.
        const ownerCognitoId = settingsRes?.payload?.cognito_id ?? null;
        const selfCognitoId = (store.getState() as any)?.authentication?.user?.cognito_id ?? null;
        const ownsIntegration = !!ownerCognitoId && !!selfCognitoId && ownerCognitoId === selfCognitoId;
        if (settingsFetched && !!settingsRes?.payload?.enabled && !ownsIntegration) {
          // warn, not log: this is the condition that makes an owner's own
          // phone go quiet, and it needs to be findable in Better Stack
          // without first enabling a debug category.
          debug.warn(`[health] sync skipped for project ${projectId}: this device does not own the integration (owner=${ownerCognitoId ?? 'none'})`);
        }

        const healthEnabled = settingsFetched && !!settingsRes?.payload?.enabled && ownsIntegration;
        if (!healthEnabled) {
          // If health integration was disabled, clean up any project-specific observers.
          Array.from(healthObserverSubscriptions.keys()).forEach((subKey) => {
            if (!subKey.startsWith(`${projectId}:`)) return;
            const existing = healthObserverSubscriptions.get(subKey);
            try {
              existing?.remove?.();
            } catch (_) {}
            healthObserverSubscriptions.delete(subKey);
          });
        }

        let readings: Record<string, any[]> = {};
        let readFailed = false;
        if (healthEnabled && nativeAvailable) {
          const selectedTypes: string[] = settingsRes.payload?.types ?? [];
          if (selectedTypes.length > 0) {
            // Register event-driven iOS listeners once per project/type.
            // This allows us to piggyback on HealthKit's own data update events.
            await ensureIOSHealthObservers(projectId, selectedTypes);

            // Read device data
            ({ readings, readFailed } = await readDeviceHealthData(selectedTypes));
          }
        }
        const hasReadings = Object.keys(readings).length > 0;
        if (!hasReadings) {
          // Throttle the settings re-fetch when there's nothing else to do this
          // cycle (disabled project, or enabled with no new device readings).
          // Avoids fanning out one /health/settings GET per project on every
          // foreground/background tick. Only throttle when the settings read
          // itself succeeded — on failure, let the next tick retry.
          //
          // `!readFailed` matters as much as `settingsFetched`. A device read
          // that THREW also produces zero readings, and stamping the throttle
          // for it bought 12 minutes of guaranteed silence on top of an error
          // nobody could see — so a broken read punished the user twice. Now a
          // failed read retries on the next focus.
          if (settingsFetched && !readFailed) {
            lastSyncAt.set(String(projectId), Date.now());
          }
          // Distinguish the reasons a caregiver would want told apart: we
          // couldn't read the device, they don't own the integration, the
          // integration is off, or there was simply nothing new.
          if (readFailed) return 'read_failed' as HealthSyncReason;
          if (!settingsFetched || !settingsRes?.payload?.enabled) return 'not_enabled' as HealthSyncReason;
          if (!ownsIntegration) return 'not_owner' as HealthSyncReason;
          return 'no_new_data' as HealthSyncReason;
        }

        let hasSuccess = false;
        if (hasReadings) {
          const syncRes = await store.dispatch(syncHealthData(projectId, readings) as any);
          if (syncRes?.success) {
            hasSuccess = true;
          }
        }
        if (hasSuccess) {
          lastSyncAt.set(String(projectId), Date.now());
          syncFailures.delete(String(projectId));
          syncBackoffUntil.delete(String(projectId));

          // Opportunistically sync clinical records (throttled to once per 24h).
          // Runs in background — does not affect the sync result or UI.
          if (healthEnabled) {
            syncClinicalRecords(projectId).catch(() => {});
          }
        } else if (hasReadings) {
          // Back off on UPLOAD failure.
          //
          // Failure used to be the ONLY outcome that left lastSyncAt unstamped,
          // so a plan whose upload was failing re-posted on every foreground
          // return, every interval tick and every background wake — with no
          // delay at all. The server side is one DynamoDB item per plan,
          // rewritten in full on each sync, so the retries land on the same hot
          // partition key that is already refusing writes and make it worse.
          // That loop inflates the call count, the failure count and the
          // contention together.
          //
          // Deliberately NOT the same as the failed-READ case above, which
          // stays unstamped on purpose: a device read that throws costs the
          // server nothing, so retrying on next focus is free. An upload that
          // fails is a request the server already rejected.
          const key = String(projectId);
          const n = (syncFailures.get(key) ?? 0) + 1;
          syncFailures.set(key, n);
          // Exponential, capped at an hour: 2m, 4m, 8m … 60m.
          const backoffMs = Math.min(SYNC_FAILURE_BACKOFF_CAP_MS, 120_000 * 2 ** (n - 1));
          syncBackoffUntil.set(key, Date.now() + backoffMs);
        }

        // Health alert observers and custom rule evaluation (iOS only, fire-and-forget).
        if (Platform.OS === 'ios') {
          const currentState = store.getState() as any;
          const currentProjects: Record<string, any> = currentState.projects?.list ?? {};
          const currentProject = Object.values(currentProjects).find(
            (p: any) => String(resolveProjectId(p)) === String(projectId),
          );
          const healthAlertsConfig = currentProject?.health_alerts_config;

          if (healthAlertsConfig?.enabled) {
            ensureHealthAlertObservers(projectId, healthAlertsConfig).catch(() => {});
            if (Object.keys(readings).length > 0) {
              evaluateCustomRules(projectId, readings).catch(() => {});
            }
          }
        }
        return hasSuccess ? ('uploaded' as HealthSyncReason) : ('upload_failed' as HealthSyncReason);
      } catch (_) {
        return 'read_failed' as HealthSyncReason;
      }
    }),
  );

  // Aggregate. With targetProjectId set — every remote-sync caller — there is
  // exactly one project, so this reports that project's reason verbatim.
  // Across several, one upload is enough to call the run a success; otherwise
  // report the first concrete reason so a caregiver is told WHY, not just
  // "nothing happened".
  const reasons = outcomes
    .map((o) => (o.status === 'fulfilled' ? o.value : undefined))
    .filter(Boolean) as HealthSyncReason[];
  if (reasons.includes('uploaded')) return { uploaded: true, reason: 'uploaded' };
  return { uploaded: false, reason: reasons[0] ?? 'no_projects' };
}
